/**
 * Post-processing for STT models that return caseless text (the en_US model
 * was seen returning everything in lowercase — "today i want…"). Everything
 * is gated behind `transcriptNeedsCapitalization`, so properly cased output
 * (en_GB, ru_RU, …) is never touched at all.
 */
import type { OutputFormat } from '../types/transcription'
import { cuesFromFormats, serializeCues, type Cue } from './subtitleConvert'

/** A cue ending in sentence-final punctuation closes its sentence — the next
 * cue starts a new one and gets a capital letter. */
const SENTENCE_END_RE = /[.!?…]["'”’)\]]*\s*$/

/** True when a transcript looks like it came from a caseless model: enough
 * letters to judge and (almost) none of them uppercase. Timed formats are
 * parsed and judged by their cue text alone — format headers (WEBVTT,
 * ASS/SSA `[Script Info]` blocks) are full of capital letters and would keep
 * a fully caseless transcript above the 2% threshold; digits and timestamps
 * are not letters and never skew the ratio. */
export function transcriptNeedsCapitalization(content: string, format?: string): boolean {
  let text = content
  if (format && format !== 'text') {
    const cues = cuesFromFormats({ [format]: content })
    if (cues) text = cues.map((c) => c.text).join('\n')
  }
  const letters = text.match(/\p{L}/gu)
  if (!letters || letters.length < 40) return false
  let upper = 0
  for (const ch of letters) if (ch !== ch.toLowerCase()) upper++
  return upper / letters.length < 0.02
}

function isEnglish(language?: string): boolean {
  return !!language && language.startsWith('en')
}

/** Uppercase the first letter of the string (identity if already uppercase). */
function capitalizeFirst(text: string): string {
  return text.replace(/\p{L}/u, (ch) => ch.toUpperCase())
}

/** Uppercase a lowercase letter that follows sentence-final punctuation. */
function capitalizeAfterPunctuation(text: string): string {
  return text.replace(
    /([.!?…]["'”’)\]]*\s+["'“‘([]*)(\p{Ll})/gu,
    (_, pre: string, ch: string) => pre + ch.toUpperCase(),
  )
}

/** English only: standalone "i" (and i'm / i've / i'll / i'd) → "I". */
function capitalizeStandaloneI(text: string): string {
  return text.replace(/(^|[^A-Za-z0-9])i(?=$|[^A-Za-z0-9])/g, '$1I')
}

export function polishPlainText(text: string, language?: string): string {
  let out = capitalizeAfterPunctuation(capitalizeFirst(text))
  if (isEnglish(language)) out = capitalizeStandaloneI(out)
  return out
}

/** Polish cue texts, carrying sentence state across cues: a cue only gets a
 * leading capital when it actually starts a sentence — capitalizing the
 * continuation of a sentence split across cues would be a new error. */
export function polishCues(cues: Cue[], language?: string): Cue[] {
  const english = isEnglish(language)
  let sentenceOpen = false
  return cues.map((cue) => {
    let text = capitalizeAfterPunctuation(cue.text)
    if (english) text = capitalizeStandaloneI(text)
    if (!sentenceOpen) text = capitalizeFirst(text)
    sentenceOpen = !SENTENCE_END_RE.test(text)
    return text === cue.text ? cue : { ...cue, text }
  })
}

/** Polish every format string in a server response: plain text directly,
 * timed formats via parse → polish → re-serialize (all 7 have parsers).
 * Call only when `transcriptNeedsCapitalization` said the transcript is
 * broken — this round-trips the server's own serialization. */
export function polishFormats(
  formats: Record<string, string>,
  language?: string,
): Record<string, string> {
  const out: Record<string, string> = {}
  for (const [fmt, content] of Object.entries(formats)) {
    if (fmt === 'text') {
      out[fmt] = polishPlainText(content, language)
      continue
    }
    const cues = cuesFromFormats({ [fmt]: content })
    out[fmt] = cues ? serializeCues(fmt as OutputFormat, polishCues(cues, language)) : content
  }
  return out
}
